import React, { useEffect, useState } from 'react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import getStripePromise from '@utils/stripePromise';

// Die erste Abo-Rechnung wird wie eine gewöhnliche Zahlung bestätigt: das
// confirmation_secret der Invoice ist ein PaymentIntent-Client-Secret.
const PaymentForm = ({ amountLabel, onPaid }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsLoading(true);
    setMessage(null);

    const payload = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Rücksprung nach 3DS/Twint — die Auswertung übernimmt die Abo-Seite.
        return_url: `${window.location.origin}/dashboard/firma/abonnement`,
      },
      redirect: 'if_required',
    });

    if (payload.error) {
      toast.error(payload.error.message);
      setMessage(payload.error.message);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    onPaid();
  };

  return (
    <form
      className="rounded-lg border border-gray-200 bg-white px-4 py-6 shadow-xs"
      onSubmit={handleSubmit}
    >
      <PaymentElement options={{ layout: 'tabs' }} />

      <button
        type="submit"
        disabled={isLoading || !stripe || !elements}
        className="mt-6 w-full rounded-md bg-svisit-primary-600 px-4 py-3 text-base font-medium text-white shadow-xs hover:bg-svisit-primary-700 disabled:cursor-not-allowed disabled:bg-gray-300"
      >
        {isLoading ? 'Verarbeitet…' : `${amountLabel} zahlungspflichtig buchen`}
      </button>

      {message && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {message}
        </p>
      )}
    </form>
  );
};

const SubscriptionCheckoutForm = ({ clientSecret, amountLabel, onPaid }) => {
  const [stripe, setStripe] = useState(null);
  const [stripeError, setStripeError] = useState(false);

  useEffect(() => {
    let active = true;
    getStripePromise()
      .then((instance) => {
        if (active) setStripe(instance);
      })
      .catch(() => {
        if (active) setStripeError(true);
      });
    return () => {
      active = false;
    };
  }, []);

  if (stripeError) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-center shadow-xs">
        <p className="text-sm text-gray-700">
          Die Zahlung konnte nicht geladen werden. Bitte prüfe deine
          Internetverbindung und lade die Seite neu.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 rounded-md bg-svisit-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-500"
        >
          Seite neu laden
        </button>
      </div>
    );
  }

  if (!stripe) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-svisit-primary-600" />
      </div>
    );
  }

  return (
    <Elements
      stripe={stripe}
      options={{ clientSecret, appearance: { theme: 'stripe' } }}
    >
      <PaymentForm amountLabel={amountLabel} onPaid={onPaid} />
    </Elements>
  );
};

export default SubscriptionCheckoutForm;
